import { useBlocker } from '@tanstack/react-router';
import { type BaseSyntheticEvent, type FC, useEffect, useRef } from 'react';
import {
  type FieldValues,
  type UseFormHandleSubmit,
  type UseFormProps,
  useForm as useFormBase,
} from 'react-hook-form';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  type CustomAlertConfig,
  useHookFormStore,
} from '@/stores/hook-form-store';
import type { DangerousAny } from '@/types/dangerous-any';

// prevent button trigger alert
export const FORM_SAFE_DATA_ATTRIBUTE = 'data-form-safe';

// force button trigger alert
export const FORM_FORCE_TRIGGER_ALERT_DATA_ATTRIBUTE =
  'data-form-force-trigger-alert';

/**
 * Return from a `handleSubmit` / `onValid` callback to skip `resetState()` after validation succeeds
 * (e.g. server rejected the save but the form values should stay on screen).
 * Plain `false` is treated the same for backward compatibility.
 */
export const SKIP_HOOK_FORM_RESET_AFTER_SUBMIT = false as const;

type FormContainerRef = HTMLDivElement | HTMLFormElement;
type SetFormContainerRef = (el: FormContainerRef | null) => void;

export const useForm = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = DangerousAny,
  TTransformedValues = TFieldValues,
>(
  props: UseFormProps<TFieldValues, TContext, TTransformedValues> & {
    customAlert?: CustomAlertConfig;
  },
) => {
  const { customAlert, ...formProps } = props;
  const setMethod = useHookFormStore((state) => state.setMethod);
  const setIsOpen = useHookFormStore((state) => state.setIsOpen);
  const setIsDirty = useHookFormStore((state) => state.setIsDirty);
  const setCustomAlert = useHookFormStore((state) => state.setCustomAlert);
  const resetState = useHookFormStore((state) => state.resetState);

  // sometimes container cannot be <form> (ex: prevent nested form)
  const formContainerRef = useRef<FormContainerRef[]>([]);
  // 目前應該只會有一個，但以防萬一先保留 array 格式
  const setFormContainerRef: SetFormContainerRef = (el) => {
    if (!el || formContainerRef.current.includes(el)) {
      return;
    }
    formContainerRef.current.push(el);
  };

  const checkElInForm = (el: HTMLElement) => {
    if (formContainerRef.current.length < 1) {
      return false;
    }
    const containers = Array.from(formContainerRef.current.values());
    return containers.some((container) => container.contains(el));
  };

  const methods = useFormBase(formProps);
  methods.subscribe({
    formState: { isDirty: true },

    callback: (formState) => {
      setMethod({
        // 強制忽略表單的 resetOptions.keepDirtyValues，確保 confirm 時一律清除 dirty
        reset: () => methods.reset(undefined, { keepDirtyValues: false }),
        checkElInForm,
      });
      setIsDirty(!!formState.isDirty);
    },
  });

  useBlocker({
    shouldBlockFn: async () => {
      if (!methods.formState.isDirty) return false;
      const confirmed = await setIsOpen('route');
      return !confirmed;
    },
    enableBeforeUnload: methods.formState.isDirty,
  });

  const handleSubmit: UseFormHandleSubmit<TFieldValues, TTransformedValues> = (
    onValid,
    onInvalid,
  ) => {
    return async (e?: BaseSyntheticEvent) => {
      await methods.handleSubmit(async (...args) => {
        const shouldResetStore = await onValid(...args);
        if (shouldResetStore !== SKIP_HOOK_FORM_RESET_AFTER_SUBMIT) {
          resetState();
        }
      }, onInvalid)(e);
    };
  };

  useEffect(() => {
    setCustomAlert(customAlert ?? null);
    return () => {
      setCustomAlert(null);
    };
  }, [customAlert, setCustomAlert]);

  return {
    ...methods,
    handleSubmit,
    setFormContainerRef,
    FormSafeButton,
    FormTriggerAlertButton,
  };
};

/**
 * 表單安全按鈕元件
 *
 * 此按鈕即使在表單編輯狀態下也不會觸發「表單未儲存」的離開提醒。
 * 適用於不影響表單資料的操作，例如：搜尋、通知、菜單等功能按鈕。
 *
 * @example
 * ```tsx
 * <FormSafeButton size="icon" variant="ghost">
 *   <Search />
 * </FormSafeButton>
 * ```
 */
export const FormSafeButton: FC<ButtonProps> = (props) => {
  return (
    <Button
      {...props}
      {...{
        [FORM_SAFE_DATA_ATTRIBUTE]: true,
      }}
    />
  );
};

/**
 * 強制觸發「表單未儲存」提醒按鈕元件
 *
 * 此按鈕在表單編輯狀態下必定會觸發「表單未儲存」的離開提醒。
 * 適用於預設不會觸發編輯中彈窗的按鈕（預設是表單內的按鈕不會觸發），例如：表單內的取消按鈕等。
 *
 * @example
 * ```tsx
 * <FormTriggerAlertButton onClick={logout}>
 *   取消
 * </FormTriggerAlertButton>
 * ```
 */
export const FormTriggerAlertButton: FC<ButtonProps> = (props) => {
  return (
    <Button
      {...props}
      {...{
        [FORM_FORCE_TRIGGER_ALERT_DATA_ATTRIBUTE]: true,
      }}
    />
  );
};

/**
 * 將按鈕轉換為表單安全按鈕
 */
export const asFormSave = () => ({
  [FORM_SAFE_DATA_ATTRIBUTE]: true,
});

/**
 * 強制讓元素在表單 dirty 時觸發「未儲存」提示。
 * 用於 form 內的非 button 可點元素（如 InteractiveDiv 列表列）。
 */
export const asFormUnsafe = () => ({
  [FORM_FORCE_TRIGGER_ALERT_DATA_ATTRIBUTE]: true,
});
